import { addIndex, compose, filter, map } from 'ramda';
import {
  addDays,
  getISOWeek,
  isAfter,
  isBefore,
  subDays,
  setYear,
} from 'date-fns';
import { format } from 'd3-format';
import { theme } from 'config';
import { ISO_Format_Date2, parseDate } from 'helpers/date';

import { VenueType } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';
import { Timeslice } from 'scenes/Report/Report.helper';

type Scale = {
  x: string | number;
  y: number;
  date: Date;
};

type BarValues = {
  date: string;
  cashbackAmount: number;
};

type MerchantTransaction = {
  merchantName: string;
  merchantId: string;
  cashbackAmount: number;
  date: string;
  totalTransactions: number;
  onlineTransactions: number;
  inStoreTransactions: number;
  totalSales: number;
  onlineSales: number;
  inStoreSales: number;
};
interface PlotFilteredByVenue {
  timeslice: Timeslice;
  totalsOnlineByYear: Scale[][];
  totalsNearbyByYear: Scale[][];
  totalTransactions: Scale[];
  height: number;
  labelFormat: string;
}
interface PlotTotals {
  timeslice: Timeslice;
  height: number;
  totalsByYear: Scale[][];
  totals: Scale[];
  labelFormat: string;
}

interface Plot {
  filterByVenueType: boolean;
  timeslice: Timeslice;
  totalsByYear: Scale[][];
  totalsOnlineByYear: Scale[][];
  totalsNearbyByYear: Scale[][];
  totalTransactions: Scale[];
  totals: Scale[];
  labelFormat: string;
  height: number;
}
interface DateRange {
  from: Date;
  to: Date;
}

const salesTransformer = (
  venue?: VenueType,
): ((list: readonly MerchantTransaction[]) => Scale[]) =>
  map(
    (barValue: MerchantTransaction): Scale => ({
      x: barValue.date,
      date: parseDate(barValue.date, ISO_Format_Date2) ?? new Date(),
      y: venue
        ? venue === VenueType.NEARBY
          ? barValue.inStoreSales
          : barValue.onlineSales
        : barValue.totalSales,
    }),
  );

const transactionsTransformer = (
  venue?: VenueType,
): ((list: readonly MerchantTransaction[]) => Scale[]) =>
  map(
    (barValue: MerchantTransaction): Scale => ({
      x: barValue.date,
      date: parseDate(barValue.date, ISO_Format_Date2) ?? new Date(),
      y: venue
        ? venue === VenueType.NEARBY
          ? barValue.inStoreTransactions
          : barValue.onlineTransactions
        : barValue.totalTransactions,
    }),
  );

const filterbyDateRange = (dateRange: DateRange) =>
  filter(({ date }: MerchantTransaction) => {
    const parsedDate = parseDate(date, ISO_Format_Date2);
    if (parsedDate) {
      return (
        isBefore(parsedDate, addDays(dateRange.to, 7)) &&
        isAfter(parsedDate, subDays(dateRange.from, 7))
      );
    }
    return false;
  });

const transactionTransformers = (
  startDate: Date,
  endDate: Date,
  venue?: VenueType,
): ((x0: MerchantTransaction[]) => Scale[]) =>
  compose<MerchantTransaction[], MerchantTransaction[], Scale[]>(
    transactionsTransformer(venue),
    filterbyDateRange({ from: startDate, to: endDate }),
  );

const saleTransformer = (
  startDate: Date,
  endDate: Date,
  venue?: VenueType,
): ((x0: MerchantTransaction[]) => Scale[]) =>
  compose<MerchantTransaction[], MerchantTransaction[], Scale[]>(
    salesTransformer(venue),
    filterbyDateRange({ from: startDate, to: endDate }),
  );

const lastElement = <T>(arr: T[]): T => arr[arr.length - 1];

const mapIndexed = addIndex<Scale>(map);

const currentYear = new Date().getFullYear().toString();

/**
 * Modify incoming Plot data, so that the output is ready to be fed to the Plot component
 * @param timeslice the time Slice: DAYLY, WEEKY, MONTHLY, or QUARTERLY
 * @returns a function that takes an array of Scales and modifies the x member according to the Time Slice
 */
const normalizeTotals = (
  timeslice: Timeslice,
): ((list: readonly Scale[]) => Scale[]) =>
  // Scale holds x and y which represent a point (x,y) that is later used by the Plot component.
  // Here however, x holds a date in 'yyyy-mm-dd' format but we just keep the week number, so we do just that
  map(({ x, ...rest }: Scale) => {
    const parsedDate = parseDate(x.toString(), ISO_Format_Date2);
    if (timeslice === Timeslice.WEEKLY && parsedDate) {
      return {
        x: getISOWeek(parsedDate),
        ...rest,
      } as Scale;
    }
    // For all other timeslices we need to bring two dates one year apart together,
    // for instance if we search for a timeframe in 2022 we'll be having data from 2021 and 2022
    // but we need to have the same *year* across the graph when it comes to plot these,
    // i.e: we have an array of data from 2022 and another from 2021,
    // but since we want to plot two bars per day we strip the first 4 characters and append the current year
    return {
      x: currentYear + x.toString().slice(4),
      ...rest,
    };
  });

const simpleLabelTransformer = (
  max: number,
  xOffset = 0,
  yOffset = 0,
  formatting = '.0f',
  data: Scale[],
) => labelTransformer(max, [], xOffset, yOffset, formatting, data);

const labelTransformer = (
  max: number,
  correctionData: Scale[] = [],
  xOffset = 0,
  yOffset = 0,
  formatting = '.0f',
  scales: Scale[],
) =>
  mapIndexed(
    ({ x, y }: Scale, index) => ({
      x,
      y:
        correctionData.length === 0
          ? y / max
          : (y - correctionData[index].y) / max,
      xOffset,
      yOffset,
      label: y !== 0 ? format(formatting)(y) : '',
      style: {
        fontSize: 10,
        stroke: 'black',
      },
    }),
    scales,
  );

const reportColors = [
  theme.violet,
  theme.btnGray,
  theme.softViolet,
  theme.softGray,
  theme.seafoamGreen,
  theme.softSeafoamGreen,
];

const shiftWeekNumbersBy1 = (scales: Scale[]) =>
  mapIndexed(({ x, y, date }: Scale, idx, list) => {
    if (
      list &&
      idx > 0 &&
      list[idx - 1].date.getFullYear() === date.getFullYear()
    ) {
      return {
        x: (x as number) + 1,
        y,
        date,
      };
    }
    return {
      x,
      y,
      date,
    };
  }, scales);

const getMaxMinLength = <T>(a: T[], b: T[]) =>
  a.length > b.length
    ? {
        max: a,
        min: b,
      }
    : { max: b, min: a };

const checkForYearsWithDifferentWeeks = (
  totalsLastYear: Scale[],
  totalsCurrentYear: Scale[],
): Scale[][] => {
  if (
    totalsCurrentYear.length === totalsLastYear.length ||
    totalsLastYear.length === 0 ||
    totalsCurrentYear.length === 0
  ) {
    return [totalsLastYear, totalsCurrentYear];
  }
  const { max: maxWeekLengthYear, min: minWeekLengthYear } = getMaxMinLength(
    totalsCurrentYear,
    totalsLastYear,
  );

  minWeekLengthYear.unshift({
    x: maxWeekLengthYear[0].x,
    y: 0,
    date: setYear(
      maxWeekLengthYear[0].date,
      minWeekLengthYear[0].date.getFullYear(),
    ),
  });
  return [
    maxWeekLengthYear,
    (shiftWeekNumbersBy1(minWeekLengthYear) as unknown) as Scale[],
  ];
};

const toStr = (x: any) => x.toString();

export {
  DateRange,
  BarValues,
  Scale,
  PlotFilteredByVenue,
  PlotTotals,
  Plot,
  MerchantTransaction,
  transactionTransformers,
  saleTransformer,
  salesTransformer,
  transactionsTransformer,
  labelTransformer,
  simpleLabelTransformer,
  reportColors,
  lastElement,
  normalizeTotals,
  shiftWeekNumbersBy1,
  checkForYearsWithDifferentWeeks,
  toStr,
};
